import React, { useState } from 'react'
import {
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Upload
} from 'antd'
import {
  PlusOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons'
import { useNavigate, Link } from 'react-router-dom'
import articleService from '../services/articleService'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import { slugify } from '../utils/format'
import { uploadSingleFile, deleteFileByUrl } from '../services/uploadService'
import { useSnackbar } from 'notistack'

const { TextArea } = Input
const { Option } = Select

const ArticleCreate: React.FC = () => {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [quillValue, setQuillValue] = useState('')
  const [quillValueEn, setQuillValueEn] = useState('')
  const [metaTitle, setMetaTitle] = useState('')
  const [metaDescription, setMetaDescription] = useState('')
  const [metaTitleEn, setMetaTitleEn] = useState('')
  const [metaDescriptionEn, setMetaDescriptionEn] = useState('')
  const [keywords] = useState<string[]>([])
  const [canonicalUrl, setCanonicalUrl] = useState('')
  const [featuredImage, setFeaturedImage] = useState<string | undefined>(
    undefined
  )
  const [socialImage, setSocialImage] = useState<string | undefined>(undefined)
  const { enqueueSnackbar } = useSnackbar()

  const onFinish = async (values: any) => {
    try {
      setLoading(true)
      const articleData = {
        ...values,
        content: quillValue,
        featuredImage: featuredImage ? { url: featuredImage, alt: values.title } : undefined,
        socialImage: socialImage ? { url: socialImage, alt: values.title } : undefined,
        metaTitle,
        metaDescription,
        keywords,
        canonicalUrl,
        translations: [
          {
            type: "en",
            title: values.title_en || '',
            description: values.description_en || '',
            content: quillValueEn,
            meta: {
              title: metaTitleEn,
              description: metaDescriptionEn
            }
          }
        ]
      };

      await articleService.createArticle(articleData)
      enqueueSnackbar('Tạo bài viết thành công', { variant: 'success' })
      navigate('/admin/articles')
    } catch (error) {
      console.error('Error creating article:', error)
      enqueueSnackbar('Tạo bài viết thất bại', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/admin/articles')
  }

  return (
    <div className='p-6'>
      <div className='mb-6'>
        <Link
          to='/admin/articles'
          className='inline-flex items-center text-blue-600 hover:text-blue-800'
        >
          <ArrowLeftOutlined className='mr-2' />
          Quay lại danh sách
        </Link>
        <h1 className='mt-4 text-2xl font-semibold text-gray-900'>
          Tạo bài viết mới
        </h1>
        <p className='mt-1 text-sm text-gray-500'>
          Thêm một bài viết mới vào hệ thống
        </p>
      </div>
      <Form
        form={form}
        layout='vertical'
        onFinish={onFinish}
        initialValues={{
          type: 'blog',
          status: 'draft'
        }}
      >
        <Row gutter={16}>
          <Col span={16}>
            <Form.Item
              name='title'
              label='Tiêu đề'
              rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
            >
              <Input
                placeholder='Nhập tiêu đề bài viết'
                onChange={e => {
                  form.setFieldsValue({
                    slug: slugify(e.target.value)
                  })
                }}
                style={{
                  width: '100%',
                  height: 32,
                  borderRadius: 4,
                  fontSize: 16,
                  padding: '6px 12px'
                }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name='slug'
              label='Slug'
              rules={[{ required: true, message: 'Vui lòng nhập slug' }]}
            >
              <Input
                placeholder='slug-bai-viet'
                style={{
                  width: '100%',
                  height: 32,
                  borderRadius: 4,
                  fontSize: 16,
                  padding: '6px 12px'
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <div style={{ marginLeft: '20px', marginTop: '20px' }}>
          <Form.Item
            name="title_en"
            label="Tiêu đề (tiếng Anh)"
            rules={[{ required: true, message: 'Vui lòng nhập tiêu đề tiếng Anh' }]}
            style={{ marginBottom: '16px' }}
          >
            <Input
              placeholder="Enter article title in English"
              style={{
                width: '100%',
                height: 32,
                borderRadius: 4,
                fontSize: 16,
                padding: '6px 12px',
                backgroundColor: '#f5f5f5',
              }}
            />
          </Form.Item>
        </div>

        <Form.Item
          name='description'
          label='Mô tả ngắn'
          rules={[{ required: true, message: 'Vui lòng nhập mô tả' }]}
        >
          <TextArea
            rows={3}
            placeholder='Nhập mô tả ngắn cho bài viết'
            maxLength={200}
            showCount
          />
        </Form.Item>

        <div style={{ marginLeft: '20px', marginTop: '-10px' }}>
          <Form.Item
            name="description_en"
            label="Mô tả ngắn (tiếng Anh)"
            rules={[{ required: true, message: 'Vui lòng nhập mô tả tiếng Anh' }]}
            style={{ marginBottom: '16px' }}
          >
            <TextArea
              rows={3}
              placeholder="Enter article description in English"
              maxLength={200}
              showCount
              style={{
                backgroundColor: '#f5f5f5',
              }}
            />
          </Form.Item>
        </div>

        <Form.Item
          name='content'
          label='Nội dung'
          rules={[{ required: true, message: 'Vui lòng nhập nội dung' }]}
          getValueFromEvent={() => quillValue}
        >
          <ReactQuill
            theme='snow'
            value={quillValue}
            onChange={val => {
              setQuillValue(val)
              form.setFieldsValue({ content: val })
            }}
            placeholder='Nhập nội dung bài viết'
            style={{
              borderRadius: '10px',
              height: '450px',
              marginBottom: '40px'
            }}
            modules={{
              toolbar: [
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [
                  { list: 'ordered' },
                  { list: 'bullet' },
                  { indent: '-1' },
                  { indent: '+1' }
                ],
                ['link', 'image'],
                ['clean']
              ]
            }}
            formats={[
              'header',
              'bold',
              'italic',
              'underline',
              'strike',
              'blockquote',
              'list',
              'bullet',
              'indent',
              'link',
              'image'
            ]}
          />
        </Form.Item>

        <div style={{ marginLeft: '20px', marginTop: '-20px' }}>
          <Form.Item
            name="content_en"
            label="Nội dung (tiếng Anh)"
            rules={[{ required: true, message: 'Vui lòng nhập nội dung tiếng Anh' }]}
            style={{ marginBottom: '40px' }}
            getValueFromEvent={() => quillValueEn}
          >
            <ReactQuill
              theme='snow'
              value={quillValueEn}
              onChange={val => {
                setQuillValueEn(val)
                form.setFieldsValue({ content_en: val })
              }}
              placeholder='Enter article content in English'
              style={{
                borderRadius: '10px',
                height: '450px',
                marginBottom: '40px',
                backgroundColor: '#f5f5f5',
              }}
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                  [
                    { list: 'ordered' },
                    { list: 'bullet' },
                    { indent: '-1' },
                    { indent: '+1' }
                  ],
                  ['link', 'image'],
                  ['clean']
                ]
              }}
              formats={[
                'header',
                'bold',
                'italic',
                'underline',
                'strike',
                'blockquote',
                'list',
                'bullet',
                'indent',
                'link',
                'image'
              ]}
            />
          </Form.Item>
        </div>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name='type'
              label='Loại bài viết'
              rules={[{ required: true, message: 'Vui lòng chọn loại' }]}
            >
              <Select placeholder='Chọn loại bài viết'>
                <Option value='blog'>Tin tức sự kiện nổi bật</Option>
                <Option value='news'>Hiểu về Lụa</Option>
                <Option value='article'>Thời trang lụa</Option>
                <Option value='heritage'>Lụa & Di sản</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name='status'
              label='Trạng thái'
              rules={[
                { required: true, message: 'Vui lòng chọn trạng thái' }
              ]}
            >
              <Select placeholder='Chọn trạng thái'>
                <Option value='draft'>Bản nháp</Option>
                <Option value='published'>Xuất bản</Option>
                <Option value='archived'>Lưu trữ</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name='readingTime'
              label='Thời gian đọc (phút)'
              rules={[
                { required: true, message: 'Vui lòng nhập thời gian đọc' }
              ]}
            >
              <Input
                type='number'
                min={1}
                placeholder='5'
                style={{
                  width: '100%',
                  height: 32,
                  borderRadius: 4,
                  fontSize: 16,
                  padding: '6px 12px'
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name='metaTitle' label='Meta Title'>
              <Input
                value={metaTitle}
                onChange={e => setMetaTitle(e.target.value)}
                maxLength={70}
                style={{
                  width: '100%',
                  height: 32,
                  borderRadius: 4,
                  fontSize: 16,
                  padding: '6px 12px'
                }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name='metaDescription' label='Meta Description'>
              <Input
                value={metaDescription}
                onChange={e => setMetaDescription(e.target.value)}
                maxLength={160}
                style={{
                  width: '100%',
                  height: 32,
                  borderRadius: 4,
                  fontSize: 16,
                  padding: '6px 12px'
                }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name='canonicalUrl' label='Canonical URL'>
              <Input
                value={canonicalUrl}
                onChange={e => setCanonicalUrl(e.target.value)}
                style={{
                  width: '100%',
                  height: 32,
                  borderRadius: 4,
                  fontSize: 16,
                  padding: '6px 12px'
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <div style={{ marginLeft: '20px', marginTop: '20px' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name='metaTitleEn' label='Meta Title (tiếng Anh)'>
                <Input
                  value={metaTitleEn}
                  onChange={e => setMetaTitleEn(e.target.value)}
                  maxLength={70}
                  style={{
                    width: '100%',
                    height: 32,
                    borderRadius: 4,
                    fontSize: 16,
                    padding: '6px 12px',
                    backgroundColor: '#f5f5f5',
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='metaDescriptionEn' label='Meta Description (tiếng Anh)'>
                <Input
                  value={metaDescriptionEn}
                  onChange={e => setMetaDescriptionEn(e.target.value)}
                  maxLength={160}
                  style={{
                    width: '100%',
                    height: 32,
                    borderRadius: 4,
                    fontSize: 16,
                    padding: '6px 12px',
                    backgroundColor: '#f5f5f5',
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Form.Item name='tags' label='Từ khóa (edit)'>
          <Select
            mode='tags'
            placeholder='Nhập từ khóa và nhấn Enter'
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item name='socialImage' label='Ảnh chia sẻ mạng xã hội'>
          <Upload
            listType='picture-card'
            fileList={
              socialImage
                ? [
                    {
                      uid: '-1',
                      name: 'social.jpg',
                      status: 'done',
                      url: socialImage,
                      thumbUrl: socialImage
                    }
                  ]
                : []
            }
            customRequest={async ({ file, onSuccess }) => {
              const res = await uploadSingleFile(file as File)
              if (res.status === 'success' && onSuccess) {
                setSocialImage(res.data.file.url)
                onSuccess('ok')
              }
            }}
            onRemove={async file => {
              if (file.url) {
                await deleteFileByUrl(file.url)
              }
              setSocialImage(undefined)
              return true
            }}
            maxCount={1}
            showUploadList={{ showRemoveIcon: true }}
          >
            {!socialImage && (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Tải lên</div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Form.Item
          name='featuredImage'
          label='Hình ảnh đại diện'
        >
          <Upload
            listType='picture-card'
            fileList={
              featuredImage
                ? [
                    {
                      uid: '-1',
                      name: 'featured.jpg',
                      status: 'done',
                      url: featuredImage,
                      thumbUrl: featuredImage
                    }
                  ]
                : []
            }
            customRequest={async ({ file, onSuccess }) => {
              const res = await uploadSingleFile(file as File)
              if (res.status === 'success' && onSuccess) {
                setFeaturedImage(res.data.file.url)
                onSuccess('ok')
              }
            }}
            onRemove={async file => {
              if (file.url) {
                await deleteFileByUrl(file.url)
              }
              setFeaturedImage(undefined)
              return true
            }}
            maxCount={1}
            showUploadList={{ showRemoveIcon: true }}
          >
            {!featuredImage && (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Tải lên</div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Form.Item className='mb-0'>
          <div className='flex gap-2 justify-end'>
            <Button onClick={handleCancel}>Hủy</Button>
            <Button type='primary' htmlType='submit' loading={loading}>
              Tạo bài viết
            </Button>
          </div>
        </Form.Item>
      </Form>
    </div>
  )
}

export default ArticleCreate
